// Detects when the deployed build advances past the version loaded at boot.
// Polls version.json on a slow timer plus tab-refocus and online-event.
// Cache-busts via a `?cb=<ts>` query param because nginx serves *.json with
// `Cache-Control: immutable` (see /etc/nginx/http.d/corovans.conf) — without
// the busted URL the browser would happily return the cached startup copy.
//
// Stops polling once an update is detected (one-shot). Network errors are
// silent — offline just means "no update detected this round".

const POLL_INTERVAL_MS = 5 * 60 * 1000;
const FIRST_CHECK_DELAY_MS = 30 * 1000;

export function startVersionCheck(currentCommit, onUpdate) {
  if (typeof window === 'undefined') return () => {};
  if (!currentCommit || currentCommit === 'unknown') return () => {};

  let detected = false;

  async function check() {
    if (detected) return;
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
    try {
      const res = await fetch(`./version.json?cb=${Date.now()}`, { cache: 'no-store' });
      if (!res.ok) return;
      const data = await res.json();
      const next = data.commit;
      if (next && next !== currentCommit) {
        detected = true;
        onUpdate({
          commit: next,
          short: data.short || next.slice(0, 7),
          version: data.version || null,
          updatedAt: data.updatedAt || null,
        });
      }
    } catch {
      // ignore — offline, blocked, parse error all collapse to "try again later"
    }
  }

  const timer = setInterval(check, POLL_INTERVAL_MS);
  const firstCheck = setTimeout(check, FIRST_CHECK_DELAY_MS);

  const onVisible = () => {
    if (typeof document !== 'undefined' && document.visibilityState === 'visible') check();
  };
  document.addEventListener('visibilitychange', onVisible);
  window.addEventListener('online', check);

  return () => {
    clearInterval(timer);
    clearTimeout(firstCheck);
    document.removeEventListener('visibilitychange', onVisible);
    window.removeEventListener('online', check);
  };
}
